import { supabase } from '@/lib/supabase';

export interface TrialInviteLink {
  id: string;
  token: string;
  label: string;
  invited_email: string | null;
  invited_phone: string | null;
  trial_days: number;
  max_uses: number | null;
  use_count: number;
  is_active: boolean;
  expires_at: string | null;
  created_by: string | null;
  created_at: string;
}

export interface TrialInviteConversion {
  id: string;
  name: string;
  created_at: string;
}

/** A trial-invite link plus any organizations that signed up through it. */
export interface TrialInviteLinkWithConversions extends TrialInviteLink {
  conversions: TrialInviteConversion[];
}

export type CreateTrialInvitePayload = {
  label: string;
  invited_email?: string | null;
  invited_phone?: string | null;
  trial_days: number;
  max_uses?: number | null;
  expires_at?: string | null;
};

/** Build the public self-serve registration URL for a token. */
export function trialInviteUrl(token: string): string {
  return `https://hub.jumpstart.co.il/register?invite=${token}`;
}

export const trialInvitesApi = {
  list: async (): Promise<TrialInviteLinkWithConversions[]> => {
    const { data, error } = await supabase
      .from('trial_invite_links')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;

    const links = (data ?? []) as TrialInviteLink[];
    if (links.length === 0) return [];

    // Attribution: organizations stamped with one of these tokens on signup.
    const tokens = links.map((l) => l.token);
    const { data: orgRows, error: orgError } = await supabase
      .from('organizations')
      .select('id, name, created_at, invite_token')
      .in('invite_token', tokens);
    if (orgError) throw orgError;

    const byToken = new Map<string, TrialInviteConversion[]>();
    for (const row of (orgRows ?? []) as Array<{
      id: string;
      name: string;
      created_at: string;
      invite_token: string | null;
    }>) {
      if (!row.invite_token) continue;
      const list = byToken.get(row.invite_token) ?? [];
      list.push({ id: row.id, name: row.name, created_at: row.created_at });
      byToken.set(row.invite_token, list);
    }

    return links.map((l) => ({
      ...l,
      conversions: byToken.get(l.token) ?? [],
    }));
  },

  create: async (payload: CreateTrialInvitePayload): Promise<TrialInviteLink> => {
    const row = {
      label: payload.label.trim(),
      invited_email: payload.invited_email?.trim() || null,
      invited_phone: payload.invited_phone?.trim() || null,
      trial_days: payload.trial_days,
      max_uses: payload.max_uses ?? null,
      expires_at: payload.expires_at || null,
    };
    const { data, error } = await supabase
      .from('trial_invite_links')
      .insert(row)
      .select('*')
      .single();
    if (error) throw error;
    return data as TrialInviteLink;
  },

  deactivate: async (id: string): Promise<void> => {
    const { error } = await supabase
      .from('trial_invite_links')
      .update({ is_active: false })
      .eq('id', id);
    if (error) throw error;
  },
};
